import styled from '@emotion/styled'
import { AvatarImage, Avatar } from 'components/core/Avatar'
import { SPACING } from 'constants/spacing.constants'
import { pxToRem } from 'utils/rem.utils'
import { Markdown } from 'components/core/Markdown'
import Typography from 'components/core/Typography'
import { memo, useEffect, useMemo, useState } from 'react'
import {
    WIDE_WIDGETS,
    WIDGET_KEY_TO_WIDGET_COMPONENT,
} from '../widgets/widget.constants'
import { DESIGN_TOKENS } from 'constants/color.constants'
import { getImageUrl } from 'utils/getImageUrl'
import {
    ChatMessageAIStreamingPayload,
    WidgetPayload,
    WilsonCustomerChatMessage,
} from '../wilsonChat.types'
import Row from 'components/core/Row'
import { DynamicChatTimestamp } from 'components/composite/DynamicChatTimestamp'
import { cn } from 'utils/cn.utils'
import { MOBILE_BREAKPOINT } from 'constants/app.constants'
import { useWebsocket } from 'hooks/useWebsocket'
import Column from 'components/core/Column'
import { CornerDownRight } from 'lucide-react'
import { SpinnerSquare } from 'components/core/SpinnerSquare'
import useEvent from 'react-use-event-hook'

const StyledWilsonChatMessageAI = styled.div`
    @keyframes slideIn {
        from {
            opacity: 0;
            transform: translateY(10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }

    width: 100%;
    display: flex;
    gap: ${SPACING.p1};

    /* Add smooth height transitions */
    * {
        transition: height 0.3s ease-out;
    }

    .wilsonChatMessageAI__avatar {
        margin-top: auto;
    }

    .wilsonChatMessageAI__body {
        display: flex;
        flex-direction: column;
        gap: ${SPACING.sm};

        .wilsonChatMessageAI__body__header {
            letter-spacing: ${pxToRem(0.12)};
        }

        .wilsonChatMessageAI__body__message {
            display: flex;
            flex-direction: column;
            max-width: ${pxToRem(400)};
            border-top-left-radius: ${pxToRem(12)};
            border-top-right-radius: ${pxToRem(12)};
            border-bottom-right-radius: ${pxToRem(12)};
            border-bottom-left-radius: ${pxToRem(4)};
            background-color: ${DESIGN_TOKENS.wilson.grey};

            &.wilsonChatMessageAI__body__message--wide {
                max-width: ${pxToRem(560)};
                min-width: ${pxToRem(560)};
            }

            .wilsonChatMessageAI__body__message__content {
                padding-top: ${SPACING.p1};
                padding-bottom: ${SPACING.p1};
                padding-left: ${SPACING.p2};
                padding-right: ${SPACING.p2};

                .wilsonChatMessageAI__body__message__content__markdown {
                    /*
                    * Styles below are required to make sure that react-markdown elements render correctly.
                    * This is because our app has style resets that end up affecting a lot of primitive components.
                    */

                    &,
                    p {
                        color: ${DESIGN_TOKENS.text.foreground};
                        font-family: Inter;
                        font-style: normal;
                        font-weight: 420;
                        line-height: ${pxToRem(20)};
                        font-size: ${pxToRem(14)};
                    }

                    ul,
                    ol {
                        list-style: initial;
                        padding-left: ${SPACING.p2};
                        padding-bottom: ${SPACING.sm};
                    }

                    h1,
                    h2,
                    h3,
                    h4 {
                        padding-top: ${SPACING.sm};
                        padding-bottom: ${SPACING.sm};
                        line-height: ${pxToRem(20)};
                        font-size: ${pxToRem(16)};
                        font-weight: 700;
                    }

                    a {
                        text-decoration: underline;
                        font-weight: 500;
                    }
                }

                .wilsonChatMessageAI__body__message__content__steps {
                    .wilsonChatMessageAI__body__message__content__steps__row {
                        animation: slideIn 0.3s ease-out;
                        transform-origin: top;

                        .wilsonChatMessageAI__body__message__content__steps__row__arrow {
                            min-width: ${pxToRem(18)};
                            margin-top: ${pxToRem(4)};
                        }
                    }
                }
            }

            .wilsonChatMessageAI__body__message__widget {
                background: ${DESIGN_TOKENS.background.secondary};
                border: 1px solid ${DESIGN_TOKENS.border.input};
                border-radius: ${pxToRem(12)};
                margin: ${SPACING.sm};
                margin-top: 0;
            }
        }
    }

    // Remove wide styling on mobile
    @media (max-width: ${MOBILE_BREAKPOINT}px) {
        .wilsonChatMessageAI__body {
            .wilsonChatMessageAI__body__message {
                &.wilsonChatMessageAI__body__message--wide {
                    max-width: 100%;
                    min-width: 100%;
                }
            }
        }
    }
`

// This needs to be a separate component to conditionally mount websocket
// usage in WilsonChatMessageAI. React hooks cannot be rendered conditionally
// in the same component. It's important to only subscribe to the websocket
// event inside of the pending message and not all agent messages.
const WilsonChatMessageAIStreamingSubscriber = ({
    pendingRequestId,
    onUpdate,
}: {
    pendingRequestId: string
    onUpdate: (streamingPayload: ChatMessageAIStreamingPayload) => void
}) => {
    useWebsocket([
        {
            events: 'pendingChatMessageStream',
            callback: (data) => {
                if (data.requestId === pendingRequestId) {
                    onUpdate({
                        response: data.response,
                        payload: data.payload as WidgetPayload<string, object>,
                        steps: data.steps,
                    })
                }
            },
        },
    ])

    return null
}

type WilsonChatMessageAIProps = {
    id: string
    chatId: string
    content: string
    createdAt?: string
    payload: WidgetPayload<string, object> | null
    isPending?: boolean
    pendingRequestId?: string
    onSendMessage: (args: {
        message: string
        payload?: WilsonCustomerChatMessage['payload']
    }) => void
    onScrollDown: () => void
}

const WILSON_INITIAL_STEP_1 = [
    'One sec',
    'Hang tight',
    'Sorry, sec',
    'On it',
    // 'Taking a closer look at your request',
    // 'Gathering all relevant information',
    // 'Reviewing the details to ensure accuracy',
    // 'Digging into the specifics to find the best approach',
    // 'Assessing your requirements carefully',
    // 'Examining every detail to get the full picture',
    // 'Exploring your request from all angles',
    // 'Evaluating your needs for a precise solution',
    // 'Pinpointing key objectives to guide our approach',
    // 'Sifting through the details to set a solid foundation',
]

const WILSON_INITIAL_STEP_2 = [
    'Thinking...',
    'Let me think...',
    'Hmm...',
    'Let me see...',
    // 'Going to the moon',
    // 'Charting the path forward',
    // 'Outlining our action plan',
    // 'Strategizing the best way to proceed',
    // 'Determining the next steps for success',
    // 'Developing a roadmap for implementation',
    // 'Coordinating resources to meet your needs',
    // 'Mapping out a clear plan of action',
    // 'Aligning priorities to streamline progress',
    // 'Establishing a timeline for execution',
    // "Preparing the blueprint for what's next",
]

export const WilsonChatMessageAI = memo(
    ({
        id,
        chatId,
        createdAt,
        pendingRequestId,
        isPending,
        content,
        payload,
        onSendMessage,
        onScrollDown,
    }: WilsonChatMessageAIProps) => {
        const [messageStreamingPayload, setMessageStreamingPayload] =
            useState<ChatMessageAIStreamingPayload>({
                response: '',
                steps: [],
                payload: null,
            })

        // Initial 2 steps are going to be rendered purely on UI side.
        // We will use a variation of different strings to make this more dynamic.
        // Later steps will come through websockets.
        const [initialSteps, setInitialSteps] = useState<
            { content: string; isDone: boolean }[]
        >([
            {
                content:
                    WILSON_INITIAL_STEP_1[
                        Math.floor(Math.random() * WILSON_INITIAL_STEP_1.length)
                    ],
                isDone: false,
            },
        ])

        // Schedule a second step after 1 second.
        // Only schedule if the message is pending.
        useEffect(() => {
            if (!isPending) {
                return
            }
            const timeoutId = setTimeout(() => {
                setInitialSteps((prev) => {
                    return [
                        { ...prev[0], isDone: true },
                        {
                            content:
                                WILSON_INITIAL_STEP_2[
                                    Math.floor(
                                        Math.random() *
                                            WILSON_INITIAL_STEP_2.length
                                    )
                                ],
                            isDone: false,
                        },
                    ]
                })
            }, 1000)

            return () => {
                clearTimeout(timeoutId)
            }
        }, [isPending])

        // Mark initial steps done if we have received steps from the backend
        useEffect(() => {
            if (!messageStreamingPayload.steps.length) {
                return
            }

            setInitialSteps((prev) => {
                return prev.map((step) => {
                    return {
                        ...step,
                        isDone: true,
                    }
                })
            })
        }, [messageStreamingPayload.steps])

        // Derive the latest payload and response from either passed props or streamed payload.
        const { responseText, responsePayload } = useMemo(() => {
            return {
                responseText: content || messageStreamingPayload.response,
                responsePayload: payload || messageStreamingPayload.payload,
            }
        }, [
            messageStreamingPayload.response,
            messageStreamingPayload.payload,
            content,
            payload,
        ])

        const _onScrollDownStable = useEvent(() => {
            onScrollDown()
        })

        // Scroll the message into view when response text or new step is received.
        useEffect(() => {
            if (pendingRequestId && isPending) {
                _onScrollDownStable()
            }
        }, [
            // This will be called when at least one of these deps update
            _onScrollDownStable,
            pendingRequestId,
            isPending,
            responseText,
            messageStreamingPayload.steps.length,
            initialSteps.length,
        ])

        const contentToRender = useMemo(() => {
            if (!responseText) {
                return (
                    <Column
                        className="wilsonChatMessageAI__body__message__content__steps"
                        gap="sm"
                    >
                        {[
                            ...initialSteps,
                            ...messageStreamingPayload.steps,
                        ].map((step, idx) => {
                            return (
                                <Row
                                    key={idx}
                                    alignItems="flex-start"
                                    gap="sm"
                                    className="wilsonChatMessageAI__body__message__content__steps__row"
                                >
                                    <CornerDownRight
                                        className="wilsonChatMessageAI__body__message__content__steps__row__arrow"
                                        color={DESIGN_TOKENS.text.foreground}
                                        size={14}
                                    />

                                    {/* We can introduce another piece of UI that will show that a step is done by using steps.isDone */}
                                    <Typography variant="body2" color="primary">
                                        {step.content}
                                    </Typography>
                                </Row>
                            )
                        })}
                        <SpinnerSquare />
                    </Column>
                )
            }
            return (
                <Markdown className="wilsonChatMessageAI__body__message__content__markdown">
                    {responseText}
                </Markdown>
            )
        }, [responseText, messageStreamingPayload.steps, initialSteps])

        const { widgetToRender, widgetToRenderClassName } = useMemo(() => {
            if (
                responsePayload &&
                typeof responsePayload === 'object' &&
                'widget' in responsePayload &&
                typeof responsePayload['widget'] === 'string'
            ) {
                const widgetKey = responsePayload[
                    'widget'
                ] as keyof typeof WIDGET_KEY_TO_WIDGET_COMPONENT

                const Component = WIDGET_KEY_TO_WIDGET_COMPONENT[widgetKey]
                if (!Component) {
                    return { widgetToRender: null, widgetToRenderClassName: '' }
                }
                const isWide = WIDE_WIDGETS.includes(widgetKey)
                return {
                    widgetToRender: (
                        <Component
                            messageId={id}
                            chatId={chatId}
                            widgetData={responsePayload['widgetData'] ?? {}}
                            onSendMessage={onSendMessage}
                        />
                    ),
                    widgetToRenderClassName: isWide
                        ? 'wilsonChatMessageAI__body__message--wide'
                        : '',
                }
            }
            return { widgetToRender: null, widgetToRenderClassName: '' }
        }, [id, responsePayload, onSendMessage, chatId])

        return (
            <>
                {isPending && pendingRequestId && (
                    <WilsonChatMessageAIStreamingSubscriber
                        pendingRequestId={pendingRequestId}
                        onUpdate={setMessageStreamingPayload}
                    />
                )}
                <StyledWilsonChatMessageAI>
                    <div className="wilsonChatMessageAI__avatar">
                        <Avatar size="sm">
                            <AvatarImage
                                src={getImageUrl('wilsonAvatar.png')}
                            />
                        </Avatar>
                    </div>
                    <div className="wilsonChatMessageAI__body">
                        <Row
                            gap="sm"
                            justifyContent="flex-start"
                            className="wilsonChatMessageAI__body__header"
                        >
                            <Typography
                                isBold
                                variant="caption"
                                color="primary"
                            >
                                Wilson · Cartage
                            </Typography>
                            <DynamicChatTimestamp dateTime={createdAt} />
                        </Row>
                        <div
                            className={cn(
                                'wilsonChatMessageAI__body__message',
                                widgetToRenderClassName
                            )}
                        >
                            <div className="wilsonChatMessageAI__body__message__content">
                                {contentToRender}
                            </div>
                            {widgetToRender && (
                                <div className="wilsonChatMessageAI__body__message__widget">
                                    {widgetToRender}
                                </div>
                            )}
                        </div>
                    </div>
                </StyledWilsonChatMessageAI>
            </>
        )
    }
)
